import { useEffect, useState } from 'react'
import { CheckCircle2, ExternalLink, Loader2, ShoppingBag, Sparkles } from 'lucide-react'
import { toast } from 'react-toastify'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { getUrlAsync } from '../lib/service-urls';
import { logError } from '../lib/betterstack-logger';

const MASKED_KEY = '••••••••••••••••••••••••••••••••••••••••••••••••'

export default function OnboardingWizard({ onClose }) {
  const { user } = useAuth()
  // Resolved from runtime config in checkAmazonStatus (OPS-391). Default true so the
  // Amazon step isn't hidden before the async URL resolves; flipped false if unset.
  const [amazonOAuthAvailable, setAmazonOAuthAvailable] = useState(true)
  const [step, setStep] = useState(1)
  const [amazonConnected, setAmazonConnected] = useState(false)
  const [checkingAmazon, setCheckingAmazon] = useState(true)
  const [amazonConnecting, setAmazonConnecting] = useState(false)
  const [auraKey, setAuraKey] = useState('')
  const [auraConnected, setAuraConnected] = useState(false)
  const [savingAura, setSavingAura] = useState(false)
  const [completing, setCompleting] = useState(false)

  useEffect(() => {
    checkAmazonStatus()
    loadAuraKey()
  }, [])

  // Auto-skip to first incomplete step; close wizard if everything's connected
  useEffect(() => {
    if (checkingAmazon) return // wait for status checks
    if (amazonConnected && auraConnected) {
      // All integrations set — mark complete and close
      completeOnboarding()
      return
    }
    if (step === 1) return // stay on welcome
     // skip Amazon
    if (step === 3 && auraConnected) setStep(4) // skip Aura
  }, [checkingAmazon, amazonConnected, amazonOAuthAvailable, auraConnected, step])

  const checkAmazonStatus = async () => {
    setCheckingAmazon(true)
    try {
      // Resolve at call time via getUrlAsync (OPS-391) — the frozen sync export is
      // empty before runtime config loads, which previously forced this off on UAT.
      const authServiceUrl = await getUrlAsync('AUTH_SERVICE_URL')
      if (!authServiceUrl) {
        setAmazonOAuthAvailable(false)
        setAmazonConnected(false)
        return
      }
      setAmazonOAuthAvailable(true)

      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return

      const response = await fetch(`${authServiceUrl}/api/auth/amazon/status`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      })
      const data = await response.json()
      setAmazonConnected(data.connected === true)
    } catch (error) {
      console.error('Failed to check Amazon status:', error)
      logError('OnboardingWizard: check Amazon status failed', { error: error.message })
      setAmazonConnected(false)
    } finally {
      setCheckingAmazon(false)
    }
  }

  const connectAmazon = async () => {
    if (!amazonOAuthAvailable) {
      toast.error('Amazon OAuth is not configured yet')
      return
    }

    setAmazonConnecting(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const authServiceUrl = await getUrlAsync('AUTH_SERVICE_URL')
      const response = await fetch(`${authServiceUrl}/api/auth/amazon/connect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      })
      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to start authorization')
      }

      window.location.href = data.authUrl
    } catch (error) {
      console.error('Amazon OAuth start error:', error)
      logError('OnboardingWizard: Amazon OAuth start failed', { error: error.message })
      toast.error(error.message)
      setAmazonConnecting(false)
    }
  }

  const loadAuraKey = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return

      const { data } = await supabase
        .from('user_api_keys')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('service', 'aura')
        .maybeSingle()

      if (data?.api_key_encrypted) {
        setAuraConnected(true)
        setAuraKey(MASKED_KEY)
      }
    } catch (error) {
      console.error('Error loading Aura key:', error)
      logError('OnboardingWizard: load Aura key failed', { error: error.message })
    }
  }

  const saveAuraKey = async () => {
    if (!auraKey.trim() || auraKey == MASKED_KEY) {
      setStep(4)
      return
    }

    setSavingAura(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const response = await fetch('/.netlify/functions/save-api-key', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ service: 'aura', apiKey: auraKey.trim() })
      })
      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save key')
      }

      setAuraConnected(true)
      setAuraKey(MASKED_KEY)
      toast.success('Aura API key saved successfully')
      setStep(4)
    } catch (error) {
      console.error('Error saving Aura key:', error)
      logError('OnboardingWizard: save Aura key failed', { error: error.message })
      toast.error(error.message)
    } finally {
      setSavingAura(false)
    }
  }

  const completeOnboarding = async () => {
    setCompleting(true)
    try {
      const { error } = await supabase.auth.updateUser({
        data: { onboarding_complete: true }
      })

      if (error) throw error

      onClose?.()
    } catch (error) {
      console.error('Failed to complete onboarding:', error)
      logError('OnboardingWizard: complete onboarding failed', { error: error.message })
      toast.error(error.message || 'Failed to complete onboarding')
    } finally {
      setCompleting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl rounded-2xl border border-theme bg-theme-surface shadow-2xl overflow-hidden">
        <div className="border-b border-theme px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-theme-tertiary">Onboarding</p>
            <h2 className="text-xl font-semibold text-theme-primary">OpSyncPro Setup Wizard</h2>
          </div>
          <div className="text-sm text-theme-tertiary">Step {Math.min(step, 4)} of 4</div>
        </div>

        <div className="px-6 py-8">
          {step === 1 && (
            <div className="space-y-6">
              <div className="w-14 h-14 rounded-2xl bg-orange-500/10 text-orange-500 flex items-center justify-center">
                <Sparkles className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-3xl font-bold text-theme-primary">Welcome to OpSyncPro</h3>
                <p className="mt-3 text-theme-secondary max-w-xl">
                  Connect the services you need to start sourcing, repricing, and managing marketplace workflows in one place.
                </p>
              </div>
              <button
                onClick={() => setStep(2)}
                className="px-5 py-3 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-medium transition-colors"
              >
                Get Started
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#FF9900]/10 text-[#FF9900] flex items-center justify-center">
                  <ShoppingBag className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-theme-primary">Connect Amazon</h3>
                  <p className="mt-2 text-theme-secondary">
                    Link your Amazon Seller Central account to unlock marketplace workflows and A2A features.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-theme bg-theme-primary p-4 flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-theme-tertiary">Connection status</p>
                  {checkingAmazon ? (
                    <p className="mt-1 text-theme-secondary flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Checking Amazon status...
                    </p>
                  ) : (
                    <p className={`mt-1 font-medium ${amazonConnected ? 'text-success' : 'text-theme-primary'}`}>
                      {amazonOAuthAvailable ? (amazonConnected ? 'Connected' : 'Not Connected') : 'Unavailable'}
                    </p>
                  )}
                </div>
                {amazonConnected && <CheckCircle2 className="w-6 h-6 text-success flex-shrink-0" />}
              </div>

              {!amazonOAuthAvailable && (
                <div className="rounded-xl border border-theme bg-theme-primary p-4">
                  <p className="text-sm text-theme-secondary">
                    Amazon OAuth is not configured for this environment yet. You can skip this step for now and connect later from Integrations.
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3">
                {amazonOAuthAvailable ? (
                  <>
                    <button
                      onClick={connectAmazon}
                      disabled={amazonConnecting || checkingAmazon}
                      className="px-5 py-3 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-medium transition-colors disabled:opacity-50"
                    >
                      <span className="flex items-center gap-2">
                        {amazonConnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                        {amazonConnecting ? 'Connecting...' : amazonConnected ? 'Reconnect Amazon' : 'Connect Amazon'}
                      </span>
                    </button>
                    <button
                      onClick={() => setStep(3)}
                      className="px-5 py-3 rounded-lg border border-theme text-theme-secondary hover:text-theme-primary hover:bg-theme-hover transition-colors"
                    >
                      Skip
                    </button>
                    {amazonConnected && (
                      <button
                        onClick={() => setStep(3)}
                        className="px-5 py-3 rounded-lg border border-theme text-theme-secondary hover:text-theme-primary hover:bg-theme-hover transition-colors"
                      >
                        Continue
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    onClick={() => setStep(3)}
                    className="px-5 py-3 rounded-lg border border-theme text-theme-secondary hover:text-theme-primary hover:bg-theme-hover transition-colors"
                  >
                    Skip for now
                  </button>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-semibold text-theme-primary">Connect Aura</h3>
                <p className="mt-2 text-theme-secondary">
                  Add your Aura API key to enable repricing and marketplace intelligence features.
                </p>
              </div>

              <div className="rounded-xl border border-theme bg-theme-primary p-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-theme-tertiary">Aura status</p>
                    <p className={`mt-1 font-medium ${auraConnected ? 'text-success' : 'text-theme-primary'}`}>
                      {auraConnected ? 'Connected' : 'Not Connected'}
                    </p>
                  </div>
                  {auraConnected && <CheckCircle2 className="w-6 h-6 text-success flex-shrink-0" />}
                </div>

                <input
                  type="password"
                  value={auraKey}
                  onChange={(e) => setAuraKey(e.target.value)}
                  onFocus={() => {
                    if (auraKey === MASKED_KEY) {
                      setAuraKey('')
                    }
                  }}
                  placeholder="Enter your Aura API key"
                  className="mt-4 w-full rounded-lg border border-theme bg-theme-surface px-3 py-2 text-theme-primary"
                />
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={saveAuraKey}
                  disabled={savingAura}
                  className="px-5 py-3 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-medium transition-colors disabled:opacity-50"
                >
                  {savingAura ? 'Saving...' : 'Save and Continue'}
                </button>
                <button
                  onClick={() => setStep(4)}
                  className="px-5 py-3 rounded-lg border border-theme text-theme-secondary hover:text-theme-primary hover:bg-theme-hover transition-colors"
                >
                  Skip
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <div className="w-14 h-14 rounded-2xl bg-success/10 text-success flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-3xl font-bold text-theme-primary">You're all set!</h3>
                <p className="mt-3 text-theme-secondary max-w-xl">
                  {user?.email ? `Your account ${user.email} is ready.` : 'Your account is ready.'} You can finish connecting services anytime from the Integrations page.
                </p>
              </div>
              <button
                onClick={completeOnboarding}
                disabled={completing}
                className="px-5 py-3 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-medium transition-colors disabled:opacity-50"
              >
                {completing ? 'Finishing...' : 'Close Wizard'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
